/**
 * Confirmation Dialog Context
 *
 * Provides a promise-based `confirm()` function that renders a proper
 * AlertDialog modal instead of the browser's native `window.confirm`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Delete this item?');
 *   if (ok) { ... }
 *
 * With options:
 *   const ok = await confirm({
 *     title: 'Delete Event',
 *     description: 'This action cannot be undone.',
 *     confirmLabel: 'Delete',
 *     variant: 'destructive',
 *   });
 *
 * @returns confirm - Async function that resolves to true (confirmed) or false (cancelled)
 *
 * CR-7271: the same dialog also serves as a modal ALERT — one OK button, no
 * cancel — for a refusal the user must read rather than glimpse in a toast:
 *   const alertDialog = useAlertDialog();
 *   await alertDialog({ title: 'Grade not saved', description: message });
 */

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { buttonVariants } from '../components/ui/button';
import { cn } from '../lib/utils';

interface ConfirmOptions {
  title?: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

type ConfirmFn = (messageOrOptions: string | ConfirmOptions) => Promise<boolean>;

interface AlertOptions {
  title?: string;
  description: string;
  okLabel?: string;
}

type AlertFn = (options: AlertOptions) => Promise<void>;

const ConfirmDialogContext = createContext<ConfirmFn | undefined>(undefined);
const AlertDialogFnContext = createContext<AlertFn | undefined>(undefined);

/**
 * Hook to show a confirmation dialog.
 * @returns confirm - Async function that shows the dialog and resolves to true/false
 */
export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmDialogContext);
  if (!context) throw new Error('useConfirm must be used within ConfirmDialogProvider');
  return context;
}

/**
 * Hook to show a modal alert: a message with a single OK button.
 * @returns alertDialog - Async function that resolves once the user dismisses it
 */
export function useAlertDialog(): AlertFn {
  const context = useContext(AlertDialogFnContext);
  if (!context) throw new Error('useAlertDialog must be used within ConfirmDialogProvider');
  return context;
}

interface ConfirmState {
  open: boolean;
  /** 'alert' hides the cancel button and resolves on any dismissal. */
  mode: 'confirm' | 'alert';
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'default' | 'destructive';
}

/**
 * Provider that renders the shared confirmation dialog.
 * Add this once near the root of the app.
 */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState>({
    open: false,
    mode: 'confirm',
    title: 'Are you sure?',
    description: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    variant: 'default',
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm: ConfirmFn = useCallback((messageOrOptions) => {
    const options: ConfirmOptions =
      typeof messageOrOptions === 'string'
        ? { description: messageOrOptions }
        : messageOrOptions;

    setState({
      open: true,
      mode: 'confirm',
      title: options.title || 'Are you sure?',
      description: options.description,
      confirmLabel: options.confirmLabel || 'Confirm',
      cancelLabel: options.cancelLabel || 'Cancel',
      variant: options.variant || 'default',
    });

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const alertDialog: AlertFn = useCallback((options) => {
    setState({
      open: true,
      mode: 'alert',
      title: options.title || 'Please note',
      description: options.description,
      confirmLabel: options.okLabel || 'OK',
      cancelLabel: '',
      variant: 'default',
    });

    return new Promise<void>((resolve) => {
      resolveRef.current = () => resolve();
    });
  }, []);

  const handleConfirm = () => {
    setState((prev) => ({ ...prev, open: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  };

  const handleCancel = () => {
    setState((prev) => ({ ...prev, open: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  };

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      <AlertDialogFnContext.Provider value={alertDialog}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => !open && handleCancel()}>
        <AlertDialogContent data-mode={state.mode}>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">{state.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {state.mode === 'confirm' && (
              <AlertDialogCancel onClick={handleCancel}>{state.cancelLabel}</AlertDialogCancel>
            )}
            <AlertDialogAction
              onClick={handleConfirm}
              className={cn(
                state.variant === 'destructive' && buttonVariants({ variant: 'destructive' })
              )}
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </AlertDialogFnContext.Provider>
    </ConfirmDialogContext.Provider>
  );
}
